<template>
  <b-card
    id="company-schedule"
    no-body
    class="company-schedule-panel company-schedule-panel--sidebar mb-0"
    :class="{ 'company-schedule-panel--mobile-collapsed': mobileCollapsed }"
  >
    <b-card-header
      class="company-schedule-panel__header d-flex align-items-center justify-content-between py-50 px-75 border-bottom"
      :class="{ 'company-schedule-panel__header--toggle': isNarrowViewport }"
      :role="isNarrowViewport ? 'button' : null"
      :tabindex="isNarrowViewport ? 0 : null"
      :aria-expanded="isNarrowViewport ? String(!mobileCollapsed) : null"
      @click="toggleMobileCollapsed"
      @keydown.enter.prevent="toggleMobileCollapsed"
      @keydown.space.prevent="toggleMobileCollapsed"
    >
      <h6 class="mb-0 company-schedule-panel__title">
        <feather-icon icon="BriefcaseIcon" size="13" class="mr-25" />
        HR
      </h6>
      <div class="d-flex align-items-center flex-shrink-0 company-schedule-panel__header-end">
        <div
          class="company-schedule-panel__actions d-flex align-items-center"
          @click.stop
        >
          <b-button
            v-if="activeTab === 0 && canDownloadAnnouncements"
            size="sm"
            variant="outline-success"
            class="company-schedule-action-btn mr-25"
            aria-label="Download Birthdays & Anniversaries"
            @click="$emit('download-announcements')"
          >
            <feather-icon icon="DownloadIcon" size="11" />
          </b-button>
          <b-button
            v-if="activeTab === 0 && canCreateAnnouncement"
            size="sm"
            variant="outline-primary"
            class="company-schedule-action-btn"
            @click="$emit('create-announcement')"
          >
            <feather-icon icon="PlusIcon" size="11" class="mr-25" />
            Add
          </b-button>
          <b-button
            v-else-if="activeTab === 2 && canCreateHoliday"
            size="sm"
            variant="outline-primary"
            class="company-schedule-action-btn"
            @click="$emit('create-holiday')"
          >
            <feather-icon icon="PlusIcon" size="11" class="mr-25" />
            Holiday
          </b-button>
          <b-button
            v-else-if="activeTab === 3 && canCreateCompanyWfh"
            size="sm"
            variant="outline-primary"
            class="company-schedule-action-btn"
            @click="$emit('create-company-wfh-day')"
          >
            <feather-icon icon="PlusIcon" size="11" class="mr-25" />
            WFH
          </b-button>
        </div>
        <feather-icon
          v-if="isNarrowViewport"
          :icon="mobileCollapsed ? 'ChevronDownIcon' : 'ChevronUpIcon'"
          size="16"
          class="text-muted company-schedule-panel__collapse-chevron flex-shrink-0"
        />
      </div>
    </b-card-header>

    <b-card-body
      v-show="!isNarrowViewport || !mobileCollapsed"
      class="p-50 company-schedule-panel__body"
      :aria-hidden="isNarrowViewport && mobileCollapsed ? 'true' : null"
    >
      <b-tabs
        v-model="activeTab"
        pills
        nav-class="company-schedule-tabs mb-50"
        content-class="company-schedule-tab-content"
      >
        <!-- Announcements -->
        <b-tab :title="announcementsTabTitle">
          <b-list-group v-if="announcements.length" flush class="company-schedule-list">
            <b-list-group-item
              v-for="(item, index) in announcements"
              :key="item.id || item.emp_id || `ann-${index}`"
              class="company-schedule-item company-schedule-item--announce"
              :class="announcementItemClass(item.type)"
            >
              <div class="company-schedule-ann-row">
                <span class="company-schedule-ann-accent" aria-hidden="true" />
                <div
                  class="company-schedule-ann-badge d-flex align-items-center justify-content-center"
                  :class="announcementBadgeClass(item.type)"
                >
                  <feather-icon :icon="announcementIcon(item.type)" size="12" />
                </div>
                <div class="company-schedule-ann-body min-w-0">
                  <div class="company-schedule-ann-title-row">
                    <span class="company-schedule-name text-truncate">{{ item.title }}</span>
                    <b-button
                      v-if="canCancelAnnouncementFn(item) && item.type === 'announcement'"
                      variant="link"
                      size="sm"
                      class="p-0 flex-shrink-0 company-schedule-cancel"
                      aria-label="Cancel announcement"
                      @click="$emit('cancel-announcement', item.id)"
                    >
                      <feather-icon icon="XIcon" size="12" class="text-danger" />
                    </b-button>
                  </div>
                  <span
                    v-if="item.message"
                    class="company-schedule-meta text-muted company-schedule-ann-msg"
                  >
                    {{ item.message }}
                  </span>
                  <span class="company-schedule-meta text-muted company-schedule-ann-date">
                    {{ announcementDisplayDate(item) }}
                  </span>
                </div>
              </div>
            </b-list-group-item>
          </b-list-group>
          <div v-else class="company-schedule-empty text-muted">
            No announcements
            <b-button
              v-if="canCreateAnnouncement"
              size="sm"
              variant="link"
              class="d-inline px-0 ml-25 company-schedule-more"
              @click="$emit('create-announcement')"
            >
              Add
            </b-button>
          </div>
        </b-tab>

        <!-- Leaves -->
        <b-tab :title="leavesTabTitle">
          <div v-if="leavesLoading" class="company-schedule-empty text-muted">Loading…</div>
          <b-list-group v-else-if="leaves.length" flush class="company-schedule-list">
            <b-list-group-item
              v-for="leave in visibleLeaves"
              :key="leave.id"
              class="company-schedule-item d-flex align-items-center justify-content-between"
              button
              @click="$emit('leave-click', leave)"
            >
              <div class="company-schedule-stack min-w-0 flex-grow-1">
                <span class="company-schedule-name text-truncate d-block">
                  {{ leaveEmployeeName(leave) }}
                </span>
                <span class="company-schedule-meta text-muted d-block">
                  {{ formatLeaveType(leave.leave_type) }}
                </span>
                <span class="company-schedule-meta text-muted d-block">
                  {{ formatDate(leave.from_date) }}
                  <template v-if="leave.from_date !== leave.to_date">
                    – {{ formatDate(leave.to_date) }}
                  </template>
                  <span class="company-schedule-days">({{ leave.total_days }}d)</span>
                </span>
              </div>
              <b-badge variant="light-primary" class="flex-shrink-0 ml-50">
                {{ formatApprovalStatus(leave.status) }}
              </b-badge>
            </b-list-group-item>
            <b-button
              v-if="leaves.length > leaveLimit"
              variant="link"
              size="sm"
              class="align-self-start px-0 py-25 company-schedule-more"
              @click="showAllLeaves = !showAllLeaves"
            >
              {{ showAllLeaves ? 'Show less' : `+${leaves.length - leaveLimit} more` }}
            </b-button>
          </b-list-group>
          <div v-else class="company-schedule-empty text-muted">No upcoming leaves</div>
        </b-tab>

        <!-- Holidays -->
        <b-tab :title="holidaysTabTitle">
          <b-list-group v-if="holidays.length" flush class="company-schedule-list">
            <b-list-group-item
              v-for="(holiday, index) in visibleHolidays"
              :key="holiday.id || `holiday-${index}`"
              class="company-schedule-item"
            >
              <div class="company-schedule-stack">
                <span class="company-schedule-name text-truncate d-block">
                  {{ holiday.holiday_name || holiday.name || holiday.holiday || 'Holiday' }}
                </span>
                <span class="company-schedule-meta text-muted d-block">
                  {{ formatDate(holiday.holiday_date || holiday.date) }}
                </span>
              </div>
            </b-list-group-item>
            <b-button
              v-if="holidays.length > holidayLimit"
              variant="link"
              size="sm"
              class="align-self-start px-0 py-25 company-schedule-more"
              @click="showAllHolidays = !showAllHolidays"
            >
              {{ showAllHolidays ? 'Show less' : `+${holidays.length - holidayLimit} more` }}
            </b-button>
          </b-list-group>
          <div v-else class="company-schedule-empty text-muted">
            No upcoming holidays
            <b-button
              v-if="canCreateHoliday"
              size="sm"
              variant="link"
              class="d-inline px-0 ml-25 company-schedule-more"
              @click="$emit('create-holiday')"
            >
              Add
            </b-button>
          </div>
        </b-tab>

        <!-- WFH -->
        <b-tab :title="wfhTabTitle">
          <b-list-group v-if="companyWfhDays.length" flush class="company-schedule-list">
            <b-list-group-item
              v-for="(day, index) in visibleWfhDays"
              :key="day.id || `wfh-${index}`"
              class="company-schedule-item"
            >
              <div class="company-schedule-stack">
                <span class="company-schedule-name text-truncate d-block">
                  {{ day.title || 'Company-Wide WFH Day' }}
                </span>
                <span class="company-schedule-meta text-muted d-block">{{ formatDate(day.date) }}</span>
              </div>
            </b-list-group-item>
            <b-button
              v-if="companyWfhDays.length > wfhLimit"
              variant="link"
              size="sm"
              class="align-self-start px-0 py-25 company-schedule-more"
              @click="showAllWfh = !showAllWfh"
            >
              {{ showAllWfh ? 'Show less' : `+${companyWfhDays.length - wfhLimit} more` }}
            </b-button>
          </b-list-group>
          <div v-else class="company-schedule-empty text-muted">
            No company WFH days
            <b-button
              v-if="canCreateCompanyWfh"
              size="sm"
              variant="link"
              class="d-inline px-0 ml-25 company-schedule-more"
              @click="$emit('create-company-wfh-day')"
            >
              Declare
            </b-button>
          </div>
        </b-tab>
      </b-tabs>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BBadge,
  BTabs,
  BTab,
  BListGroup,
  BListGroupItem,
} from 'bootstrap-vue'

export default {
  name: 'CompanySchedulePanel',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BBadge,
    BTabs,
    BTab,
    BListGroup,
    BListGroupItem,
  },
  props: {
    announcements: { type: Array, default: () => [] },
    leaves: { type: Array, default: () => [] },
    holidays: { type: Array, default: () => [] },
    companyWfhDays: { type: Array, default: () => [] },
    leavesLoading: { type: Boolean, default: false },
    canCreateAnnouncement: { type: Boolean, default: false },
    canDownloadAnnouncements: { type: Boolean, default: false },
    canCancelAnnouncement: { type: Function, default: null },
    canCreateHoliday: { type: Boolean, default: false },
    canCreateCompanyWfh: { type: Boolean, default: false },
    formatDate: { type: Function, required: true },
    formatDateMonthOnly: { type: Function, default: null },
    formatLeaveType: { type: Function, default: v => v || 'Leave' },
    formatApprovalStatus: { type: Function, default: v => v || '' },
    getUserName: { type: Function, default: null },
    leaveLimit: { type: Number, default: 5 },
    holidayLimit: { type: Number, default: 5 },
    wfhLimit: { type: Number, default: 5 },
  },
  data() {
    const narrow = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(max-width: 991.98px)').matches
      : false
    return {
      activeTab: 0,
      showAllLeaves: false,
      showAllHolidays: false,
      showAllWfh: false,
      /** Phone/tablet (≤991): stays collapsed until user toggles — match Org Snapshot */
      mobileCollapsed: true,
      isNarrowViewport: narrow,
    }
  },
  mounted() {
    if (typeof window === 'undefined' || !window.matchMedia) return
    this._panelMq = window.matchMedia('(max-width: 991.98px)')
    this._onPanelMq = e => {
      const nowNarrow = e.matches
      if (nowNarrow && !this.isNarrowViewport) {
        this.mobileCollapsed = true
      }
      this.isNarrowViewport = nowNarrow
    }
    this.isNarrowViewport = this._panelMq.matches
    if (this.isNarrowViewport) {
      this.mobileCollapsed = true
    }
    if (this._panelMq.addEventListener) {
      this._panelMq.addEventListener('change', this._onPanelMq)
    } else if (this._panelMq.addListener) {
      this._panelMq.addListener(this._onPanelMq)
    }
  },
  beforeDestroy() {
    if (!this._panelMq || !this._onPanelMq) return
    if (this._panelMq.removeEventListener) {
      this._panelMq.removeEventListener('change', this._onPanelMq)
    } else if (this._panelMq.removeListener) {
      this._panelMq.removeListener(this._onPanelMq)
    }
  },
  computed: {
    announcementsTabTitle() {
      const n = (this.announcements || []).length
      return n ? `Announce (${n})` : 'Announce'
    },
    leavesTabTitle() {
      const n = (this.leaves || []).length
      return n ? `Leaves (${n})` : 'Leaves'
    },
    holidaysTabTitle() {
      const n = (this.holidays || []).length
      return n ? `Holidays (${n})` : 'Holidays'
    },
    wfhTabTitle() {
      const n = (this.companyWfhDays || []).length
      return n ? `WFH (${n})` : 'WFH'
    },
    visibleLeaves() {
      return this.showAllLeaves
        ? this.leaves
        : this.leaves.slice(0, this.leaveLimit)
    },
    visibleHolidays() {
      return this.showAllHolidays
        ? this.holidays
        : this.holidays.slice(0, this.holidayLimit)
    },
    visibleWfhDays() {
      return this.showAllWfh
        ? this.companyWfhDays
        : this.companyWfhDays.slice(0, this.wfhLimit)
    },
  },
  methods: {
    toggleMobileCollapsed() {
      if (!this.isNarrowViewport) return
      this.mobileCollapsed = !this.mobileCollapsed
    },
    leaveEmployeeName(leave) {
      if (!leave) return 'Employee'
      if (typeof this.getUserName === 'function') {
        return this.getUserName(leave.emp_id) || leave.emp_id || 'Employee'
      }
      return leave.emp_id || 'Employee'
    },
    canCancelAnnouncementFn(item) {
      return typeof this.canCancelAnnouncement === 'function'
        ? this.canCancelAnnouncement(item)
        : false
    },
    announcementItemClass(type) {
      if (type === 'birthday') return 'is-birthday'
      if (type === 'anniversary') return 'is-anniversary'
      return ''
    },
    announcementBadgeClass(type) {
      if (type === 'birthday') return 'badge-birthday'
      if (type === 'anniversary') return 'badge-anniversary'
      return 'badge-default'
    },
    announcementIcon(type) {
      if (type === 'birthday') return 'HeartIcon'
      if (type === 'anniversary') return 'AwardIcon'
      return 'BellIcon'
    },
    announcementDisplayDate(item) {
      try {
        if (item.type === 'birthday' || item.type === 'anniversary') {
          const d = item.birthday_date || item.anniversary_date || item.date
          return typeof this.formatDateMonthOnly === 'function'
            ? this.formatDateMonthOnly(d)
            : (d || '')
        }
        const d = item.created_at || item.date
        return typeof this.formatDate === 'function' ? this.formatDate(d) : (d || '')
      } catch (e) {
        return item.created_at || item.date || ''
      }
    },
    /** Switch tab: 0=News, 1=Leaves, 2=Holidays, 3=WFH */
    open(tabIndex) {
      if (typeof tabIndex === 'number' && tabIndex >= 0) {
        this.activeTab = tabIndex
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.company-schedule-panel {
  background: #ffffff;
  border: 1px solid #e8ecf0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.company-schedule-panel__header {
  background: linear-gradient(180deg, #f0fafb 0%, #f7fbfc 100%);
  flex-shrink: 0;
  min-height: 0;
  gap: 0.35rem;
}

.company-schedule-panel__title {
  font-size: 0.65rem;
  font-weight: 700;
  color: #0f766e;
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
  line-height: 1.15;
}

.company-schedule-panel__header-end {
  gap: 0.35rem;
  margin-left: auto;
  min-width: 0;
}

.company-schedule-panel__actions {
  gap: 0.2rem;
  flex-shrink: 0;
}

.company-schedule-action-btn {
  font-size: 0.52rem;
  padding: 0.12rem 0.3rem;
  line-height: 1.1;
  font-weight: 600;
  white-space: nowrap;
  border-radius: 4px;
}

.company-schedule-panel__body {
  flex: 0 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* BTabs renders .company-schedule-tabs on an inner <ul> — ::v-deep must lead the selector */
::v-deep .company-schedule-tabs {
  flex-wrap: nowrap;
  gap: 0.1rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 0.25rem !important;
}

::v-deep .company-schedule-tabs .nav-item {
  flex: 1 1 0;
  min-width: 0;
  margin-bottom: 0;
}

::v-deep .company-schedule-tabs .nav-link {
  font-size: 0.62rem !important;
  font-weight: 600;
  padding: 0.2rem 0.12rem !important;
  text-align: center;
  white-space: nowrap;
  border-radius: 4px !important;
  color: #374151;
  line-height: 1.2;
  min-height: 0;
}

::v-deep .company-schedule-tabs .nav-link.active {
  background-color: #0099AC !important;
  color: #fff !important;
  padding: 0.2rem 0.12rem !important;
  box-shadow: 0 1px 2px rgba(0, 153, 172, 0.35);
}

.company-schedule-tab-content {
  max-height: 280px;
  overflow-y: auto;
  flex: 0 1 auto;
  min-height: 0;
}

.company-schedule-tab-content::-webkit-scrollbar {
  width: 3px;
}

.company-schedule-tab-content::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.company-schedule-list :deep(.list-group-item) {
  background: #fafbfc;
  border: 1px solid #f0f2f5 !important;
  border-radius: 6px !important;
  margin-bottom: 0.25rem;
  padding: 0.4rem 0.4rem !important;
}

.company-schedule-list :deep(.list-group-item:last-of-type) {
  margin-bottom: 0;
}

.company-schedule-stack {
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
  min-width: 0;
}

.company-schedule-name {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
  margin: 0;
}

.company-schedule-meta {
  font-size: 0.72rem;
  line-height: 1.35;
  margin: 0;
}

.company-schedule-days {
  color: #0099AC;
  font-weight: 600;
  margin-left: 0.15rem;
}

.company-schedule-empty {
  font-size: 0.65rem;
  padding: 0.3rem 0.1rem;
  line-height: 1.35;
}

.company-schedule-more {
  font-size: 0.62rem;
  font-weight: 600;
  color: #0099AC;
}

.company-schedule-cancel {
  line-height: 1;
  min-width: 0;
  margin: 0;
  padding: 0;
}

/* Announce row: [accent | icon | text stack] */
.company-schedule-item--announce {
  position: relative;
  overflow: hidden;
  padding: 0.45rem 0.45rem 0.45rem 0.6rem !important;
}

.company-schedule-ann-row {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
}

.company-schedule-ann-accent {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  border-radius: 6px 0 0 6px;
  background: #0099AC;
}

.company-schedule-item--announce.is-birthday .company-schedule-ann-accent {
  background: #ec4899;
}

.company-schedule-item--announce.is-anniversary .company-schedule-ann-accent {
  background: #f59e0b;
}

.company-schedule-ann-badge {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border-radius: 5px;
  background: rgba(0, 153, 172, 0.12);
  color: #0099AC;
}

.company-schedule-ann-badge.badge-birthday {
  background: rgba(236, 72, 153, 0.12);
  color: #db2777;
}

.company-schedule-ann-badge.badge-anniversary {
  background: rgba(245, 158, 11, 0.12);
  color: #d97706;
}

.company-schedule-ann-body {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  flex: 1 1 auto;
  min-width: 0;
}

.company-schedule-ann-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.25rem;
  min-width: 0;
}

.company-schedule-ann-msg {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.company-schedule-ann-date {
  display: block;
}

.min-w-0 {
  min-width: 0;
}

/* Stacked under Org Leaves (≤991): content-sized, not stretched 9+3 height */
@media (max-width: 991px) {
  .company-schedule-panel {
    height: auto;
    max-height: none;
  }

  .company-schedule-panel__header {
    flex-wrap: nowrap;
    gap: 0.4rem;
  }

  .company-schedule-panel__header--toggle {
    cursor: pointer;
    user-select: none;
    min-height: 2.5rem;
  }

  .company-schedule-panel__header--toggle:hover {
    background: linear-gradient(180deg, #e8f6f8 0%, #f0fafb 100%);
  }

  .company-schedule-panel__header--toggle:focus {
    outline: none;
    box-shadow: inset 0 0 0 2px rgba(0, 172, 153, 0.25);
  }

  .company-schedule-panel__header-end {
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .company-schedule-panel__actions {
    flex-wrap: wrap;
  }

  .company-schedule-panel__collapse-chevron {
    margin-left: 0.1rem;
  }

  .company-schedule-panel--mobile-collapsed {
    height: auto !important;
    min-height: 0 !important;
  }

  .company-schedule-panel.company-schedule-panel--mobile-collapsed > .card-body,
  .company-schedule-panel.company-schedule-panel--mobile-collapsed .company-schedule-panel__body {
    display: none !important;
  }

  .company-schedule-action-btn {
    font-size: 0.6rem;
    padding: 0.25rem 0.4rem;
    min-height: 1.85rem;
  }

  .company-schedule-tab-content {
    max-height: 320px;
  }

  ::v-deep .company-schedule-tabs .nav-link {
    font-size: 0.68rem !important;
    padding: 0.3rem 0.2rem !important;
  }
}

@media (max-width: 575px) {
  .company-schedule-tab-content {
    max-height: 280px;
  }

  .company-schedule-name {
    font-size: 0.8rem;
  }
}
</style>
